import * as React from "react";
import PropTypes from "prop-types";
import StorageUtils from "@src/utils/storageUtils";
import { getBehaviourColourForPosition } from "@signolteam/shared/colours";
import { useAuthContext } from "./authContext";
import { useDCContext } from "./dcContext";

/**
 * @type {React.Context<{
 *   profile: {appUser: any, behaviours: any[], charities: any[], teams: any[], customer: any, manager: any, managerMetadata: any[]}
 *   selectedPeriod: string;
 *   setSelectedPeriod: (s) => void;
 *   selectedBehaviour: any;
 *   selectedBehaviourColour: ReturnType<typeof getBehaviourColourForPosition>;
 *   selectedTeam: any;
 *   setSelectedTeam: (t) => void;
 * }>}
 */
const ManagerContext = React.createContext();
ManagerContext.displayName = "ManagerContext";

const useManagerContext = () => React.useContext(ManagerContext);

const ManagerContentProvider = ({ children, managerData }) => {
  const { user, avatarInitials, setAvatarInitials } = useAuthContext();
  const profile = React.useMemo(() => managerData, [managerData]);
  const { getDynamicReplacement, setProfileForDC, setSelectedBehaviourForDC } =
    useDCContext();
  setProfileForDC(profile); // Set the profile in DC context so that dynamic replacements depending on profile data are updated across the app
  const [selectedPeriod, setSelectedPeriod] = React.useState("all");
  const [selectedBehaviour, setSelectedBehaviourForContext] = React.useState(
    profile?.behaviours?.[0],
  );
  setSelectedBehaviourForDC(profile?.behaviours?.[0]); // Set the selected behaviour in DC context so that dynamic replacements depending on selected behaviour are updated across the app
  const [selectedTeam, setSelectedTeam] = React.useState(null);

  const getFirstName = React.useCallback(() => {
    if (profile?.appUser?.firstname) {
      return profile.appUser.firstname;
    }
    if (user?.firstname) {
      return user.firstname;
    }
    return "No Name";
  }, [profile?.appUser?.firstname, user.firstname]);

  const getLastName = React.useCallback(() => {
    if (profile?.appUser?.lastname) {
      return profile.appUser.lastname;
    }
    if (user?.lastname) {
      return user.lastname;
    }
    return "No Last Name";
  }, [profile?.appUser?.lastname, user.lastname]);

  React.useEffect(() => {
    const expected = `${getFirstName()[0] || "N"}${getLastName()[0] || "N"}`;
    if (avatarInitials !== expected) {
      setAvatarInitials(expected);
    }
  }, [getFirstName, getLastName, avatarInitials, setAvatarInitials]);

  //Helper memos
  const selectedBehaviourColour = React.useMemo(
    () => getBehaviourColourForPosition(selectedBehaviour?.order),
    [selectedBehaviour],
  );

  const setSelectedBehaviour = (behaviour) => {
    setSelectedBehaviourForContext(behaviour);
    setSelectedBehaviourForDC(behaviour); // also update the selected behaviour in DC context so that dynamic replacements depending on selected behaviour are updated across the app
  };
  const contextValues = React.useMemo(
    () => ({
      profile,
      getFirstName,
      getLastName,
      selectedPeriod,
      setSelectedPeriod,
      selectedBehaviour,
      setSelectedBehaviour,
      selectedTeam,
      setSelectedTeam,
      selectedBehaviourColour,
    }),
    [
      profile,
      getFirstName,
      getLastName,
      selectedPeriod,
      selectedBehaviour,
      selectedTeam,
      selectedBehaviourColour,
    ],
  );

  return (
    <ManagerContext.Provider value={contextValues}>
      {children}
    </ManagerContext.Provider>
  );
};

ManagerContentProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export { useManagerContext };

export default ManagerContentProvider;
